import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import VisualizationRenderer from '../visualizations/VisualizationRenderer';
import mainLogo from '../../assets/images/mainlogo.png';

// Strip XML tool call tags from AI messages
const cleanAIContent = (content) => {
  if (!content || typeof content !== 'string') return content;
  let cleaned = content.replace(/<\/?(?:get_stock_price|get_historical_prices|symbol|days|get_portfolio|analyze_stock|search_stocks|get_market_overview|get_asset_details|tool_call|function_call|parameters|parameter)[^>]*>/gi, '');
  cleaned = cleaned.replace(/<\/?[a-z_]{3,}[^>]*>/g, '');
  cleaned = cleaned.replace(/\n{3,}/g, '\n\n').trim();
  return cleaned;
};

const ChatMessage = ({ message, onRegenerate, onCopy, userName }) => {
  const [copied, setCopied] = useState(false);

  const isAI = message.type === 'ai' || message.type === 'gideon';
  const isUser = message.type === 'user';
  const isNudge = message.type === 'nudge';

  // Clean content for AI messages
  const displayContent = isAI ? cleanAIContent(message.content) : message.content;

  const hasVisualizations = Array.isArray(message.visualizations) && message.visualizations.length > 0;
  const referencesMissingChart = typeof displayContent === 'string'
    && /\b(?:chart|graph)s?\s+(?:above|below)\b|\bhere(?:'s|s| is| are)?\s+(?:the|a|an|your)\s+[^.!?]{0,30}?(?:chart|graph)s?\b/i.test(displayContent);

  // Get user initial for avatar
  const getUserInitial = () => {
    if (userName && typeof userName === 'string' && userName.trim()) {
      return userName.trim().charAt(0).toUpperCase();
    }
    return 'U';
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  // Handle copy message
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      onCopy?.(message);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  // Handle regenerate
  const handleRegenerate = () => {
    onRegenerate?.(message);
  };

  // Nudge messages have special styling
  if (isNudge) {
    return (
      <div style={{
        marginBottom: '1.5rem',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        width: '100%'
      }}>
        <div style={{
          width: '36px',
          height: '36px',
          flexShrink: 0,
          background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.2), rgba(245, 158, 11, 0.2))',
          border: '1px solid rgba(251, 191, 36, 0.4)',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '18px'
        }}>
          💡
        </div>
        <div style={{
          flex: 1,
          background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.1) 0%, rgba(251, 191, 36, 0.05) 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(251, 191, 36, 0.3)',
          borderRadius: '16px',
          padding: '16px',
          fontSize: '0.9375rem',
          lineHeight: '1.7',
          color: 'rgba(255, 255, 255, 0.9)'
        }}>
          {displayContent}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`chat-message ${isAI ? 'gideon' : 'user'}`}
      style={{
        marginBottom: '1.5rem',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        flexDirection: isUser ? 'row-reverse' : 'row',
        width: '100%'
      }}
    >
      {/* Avatar */}
      <div style={{
        width: '36px',
        height: '36px',
        flexShrink: 0,
        background: isAI ? '#FFFFFF' : 'linear-gradient(135deg, #00e1c4 0%, #00b89c 100%)',
        border: isAI
          ? '2px solid #000000'
          : '2px solid rgba(0, 225, 196, 0.6)',
        borderRadius: isAI ? '4px' : '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: isAI ? '2px 2px 0px #333333' : '0 2px 8px rgba(0, 225, 196, 0.3)',
        padding: isAI ? '3px' : '0',
        fontSize: isUser ? '1rem' : 'inherit',
        fontWeight: isUser ? '700' : 'normal',
        color: isUser ? '#0a0d14' : 'inherit'
      }}>
        {isAI ? (
          <img src={mainLogo} alt="Gideon" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        ) : getUserInitial()}
      </div>

      {/* Message Content — full width when it carries a chart so the
          visualization isn't squeezed into (and clipped by) the bubble */}
      <div style={{
        flex: 1,
        minWidth: 0,
        maxWidth: hasVisualizations ? '100%' : '75%'
      }}>
        <div style={{
          background: isAI
            ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.08) 100%)'
            : 'linear-gradient(135deg, rgba(0, 225, 196, 0.25) 0%, rgba(0, 225, 196, 0.35) 100%)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: isAI
            ? '1px solid rgba(255, 255, 255, 0.15)'
            : '1px solid rgba(0, 225, 196, 0.4)',
          borderRadius: isAI ? '16px' : '16px 16px 4px 16px',
          boxShadow: isAI
            ? '0 10px 30px rgba(0, 0, 0, 0.3)'
            : '0 4px 16px rgba(0, 225, 196, 0.2)',
          padding: '16px',
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.2s ease'
        }}>
          {/* Message Header (for AI) */}
          {isAI && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '8px'
            }}>
              <div style={{
                fontSize: '0.75rem',
                fontWeight: '700',
                color: '#00e1c4',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                textShadow: '0 0 10px rgba(0, 225, 196, 0.3)'
              }}>
                GIDEON
              </div>
              <div style={{
                fontSize: '0.75rem',
                color: 'rgba(255, 255, 255, 0.5)'
              }}>
                {formatTime(message.timestamp)}
              </div>
            </div>
          )}

          {/* Message Content */}
          <div style={{
            fontSize: '0.9375rem',
            lineHeight: '1.7',
            color: isAI ? 'rgba(255, 255, 255, 0.9)' : '#ffffff',
            wordWrap: 'break-word'
          }}>
            {isAI ? (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: ({node, ...props}) => <h1 style={{fontSize: '1.5rem', fontWeight: '700', marginTop: '1rem', marginBottom: '0.75rem', color: '#00e1c4'}} {...props} />,
                  h2: ({node, ...props}) => <h2 style={{fontSize: '1.25rem', fontWeight: '700', marginTop: '1rem', marginBottom: '0.5rem', color: '#00e1c4'}} {...props} />,
                  h3: ({node, ...props}) => <h3 style={{fontSize: '1.1rem', fontWeight: '600', marginTop: '0.75rem', marginBottom: '0.5rem', color: '#5fffe6'}} {...props} />,
                  p: ({node, ...props}) => <p style={{marginBottom: '1rem', lineHeight: '1.7'}} {...props} />,
                  strong: ({node, ...props}) => <strong style={{fontWeight: '700', color: '#00e1c4'}} {...props} />,
                  em: ({node, ...props}) => <em style={{fontStyle: 'italic', color: '#5fffe6'}} {...props} />,
                  ul: ({node, ...props}) => <ul style={{marginLeft: '1.5rem', marginBottom: '1rem', listStyleType: 'disc'}} {...props} />,
                  ol: ({node, ...props}) => <ol style={{marginLeft: '1.5rem', marginBottom: '1rem', listStyleType: 'decimal'}} {...props} />,
                  li: ({node, ...props}) => <li style={{marginBottom: '0.5rem', paddingLeft: '0.25rem'}} {...props} />,
                  code: ({node, inline, ...props}) =>
                    inline ? (
                      <code style={{
                        background: 'rgba(0, 225, 196, 0.1)',
                        border: '1px solid rgba(0, 225, 196, 0.2)',
                        borderRadius: '4px',
                        padding: '2px 6px',
                        fontSize: '0.875rem',
                        fontFamily: 'Monaco, Consolas, monospace',
                        color: '#5fffe6'
                      }} {...props} />
                    ) : (
                      <code style={{
                        display: 'block',
                        background: 'rgba(10, 13, 20, 0.6)',
                        border: '1px solid rgba(0, 225, 196, 0.2)',
                        borderRadius: '8px',
                        padding: '1rem',
                        marginBottom: '1rem',
                        fontSize: '0.875rem',
                        fontFamily: 'Monaco, Consolas, monospace',
                        overflowX: 'auto',
                        color: '#e2e8f0'
                      }} {...props} />
                    ),
                  blockquote: ({node, ...props}) => <blockquote style={{
                    borderLeft: '4px solid #00e1c4',
                    paddingLeft: '1rem',
                    marginLeft: '0',
                    marginBottom: '1rem',
                    fontStyle: 'italic',
                    color: 'rgba(255, 255, 255, 0.7)'
                  }} {...props} />,
                  a: ({node, ...props}) => <a style={{
                    color: '#5fffe6',
                    textDecoration: 'underline',
                    textUnderlineOffset: '2px',
                    cursor: 'pointer'
                  }} target="_blank" rel="noopener noreferrer" {...props} />
                }}
              >
                {displayContent}
              </ReactMarkdown>
            ) : (
              <div style={{whiteSpace: 'pre-wrap'}}>{displayContent}</div>
            )}
          </div>

          {/* Gideon is instructed never to refuse a chart, so it can describe
              one the backend failed to produce. Say so rather than leaving the
              reader hunting for a chart that was never sent. */}
          {!isUser && !hasVisualizations && referencesMissingChart && (
            <div style={{
              marginTop: '12px',
              padding: '10px 12px',
              borderRadius: '8px',
              border: '1px dashed rgba(255, 255, 255, 0.25)',
              background: 'rgba(255, 255, 255, 0.04)',
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: '0.8125rem'
            }}>
              📉 The chart couldn't be loaded right now — the numbers above are still accurate.
            </div>
          )}

          {/* User Message Timestamp */}
          {isUser && (
            <div style={{
              marginTop: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: '8px',
              fontSize: '0.75rem',
              color: 'rgba(255, 255, 255, 0.6)'
            }}>
              <span>{formatTime(message.timestamp)}</span>
            </div>
          )}

          {/* Action Buttons (for AI messages) */}
          {isAI && (
            <div style={{
              display: 'flex',
              gap: '6px',
              marginTop: '12px',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={handleCopy}
                style={{
                  padding: '8px 12px',
                  background: copied ? 'rgba(0, 225, 196, 0.2)' : 'rgba(0, 225, 196, 0.1)',
                  border: '1px solid rgba(0, 225, 196, 0.3)',
                  borderRadius: '8px',
                  color: '#00e1c4',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  minHeight: '32px'
                }}
              >
                <span>{copied ? '✓' : '📋'}</span>
                <span>{copied ? 'Copied!' : 'Copy'}</span>
              </button>

              {onRegenerate && (
                <button
                  onClick={handleRegenerate}
                  style={{
                    padding: '8px 12px',
                    background: 'rgba(0, 225, 196, 0.1)',
                    border: '1px solid rgba(0, 225, 196, 0.3)',
                    borderRadius: '8px',
                    color: '#00e1c4',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    minHeight: '32px'
                  }}
                >
                  <span>🔄</span>
                  <span>Regenerate</span>
                </button>
              )}
            </div>
          )}
        </div>

        {/* Visualizations — rendered below the bubble at full column width;
            inside it they were clipped by the bubble's overflow:hidden */}
        {hasVisualizations && (
          <div style={{ marginTop: '12px' }}>
            <VisualizationRenderer visualizations={message.visualizations} forceDark />
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatMessage;
